/** Pure, side-effect-free model helpers: id/clock, normalisation, duplication,
 *  and import-conflict analysis. Kept free of IndexedDB so they're trivially
 *  unit-testable and reusable by the future Cloudflare sync layer. */
import type {
  Bundle,
  ConflictChoice,
  ImportItem,
  PatternRecord,
} from '../domain/types';

export const SCHEMA = 3;
export const TRASH_RETENTION_DAYS = 365;
export const DIM_MIN = 4;
export const DIM_MAX = 80;

export const nowISO = () => new Date().toISOString();
const daysBetween = (a: string, b: string) => (+new Date(b) - +new Date(a)) / 86_400_000;

let counter = 0;
export function uid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return 'p_' + crypto.randomUUID().slice(0, 18);
  } catch {
    /* ignore */
  }
  return 'p_' + Date.now().toString(36) + (counter++).toString(36);
}

export const clampDim = (n: number) => Math.max(DIM_MIN, Math.min(DIM_MAX, Math.round(n) || 25));

export function normalize(rec: Partial<PatternRecord>): PatternRecord {
  const t = nowISO();
  return {
    id: rec.id || uid(),
    name: rec.name || 'Untitled pattern',
    description: rec.description || '',
    fields: rec.fields && typeof rec.fields === 'object' ? rec.fields : {},
    cols: clampDim(rec.cols ?? 25),
    rows: clampDim(rec.rows ?? 25),
    gauge: rec.gauge || 'dk',
    cells: rec.cells && typeof rec.cells === 'object' ? rec.cells : {},
    palette: Array.isArray(rec.palette) ? rec.palette : [],
    status: rec.status || 'active',
    createdAt: rec.createdAt || t,
    updatedAt: rec.updatedAt || t,
    archivedAt: rec.archivedAt ?? null,
    trashedAt: rec.trashedAt ?? null,
    thumb: rec.thumb ?? null,
  };
}

export function duplicateRecord(src: PatternRecord, newName?: string): PatternRecord {
  const t = nowISO();
  return normalize({
    ...structuredClone(src),
    id: uid(),
    name: newName || src.name + ' copy',
    status: 'active',
    createdAt: t,
    updatedAt: t,
    archivedAt: null,
    trashedAt: null,
  });
}

export function trashDaysLeft(rec: PatternRecord): number | null {
  if (!rec.trashedAt) return null;
  return Math.max(0, Math.ceil(TRASH_RETENTION_DAYS - daysBetween(rec.trashedAt, nowISO())));
}
export function isExpiredTrash(rec: PatternRecord): boolean {
  return (
    rec.status === 'trashed' && !!rec.trashedAt && daysBetween(rec.trashedAt, nowISO()) > TRASH_RETENTION_DAYS
  );
}

function stripVolatile(rec: PatternRecord) {
  const { updatedAt: _u, thumb: _t, ...rest } = rec;
  return rest;
}

export interface ImportAnalysis {
  ok: boolean;
  error?: string;
  kind?: string;
  items: ImportItem[];
}

/** Classify each incoming record against the existing library by id. */
export function analyzeImport(
  input: string | Bundle | PatternRecord[] | PatternRecord,
  existingById: (id: string) => PatternRecord | null
): ImportAnalysis {
  let obj: unknown;
  if (typeof input === 'string') {
    try {
      obj = JSON.parse(input);
    } catch {
      return { ok: false, error: 'That file is not valid JSON.', items: [] };
    }
  } else {
    obj = input;
  }

  let incoming: unknown[] = [];
  let kind = 'library';
  if (Array.isArray(obj)) incoming = obj;
  else if (obj && typeof obj === 'object' && Array.isArray((obj as Bundle).patterns)) {
    incoming = (obj as Bundle).patterns;
    kind = (obj as Bundle).kind || 'library';
  } else if (obj && typeof obj === 'object' && 'id' in (obj as object)) {
    incoming = [obj];
  }
  if (!incoming.length) return { ok: false, error: 'No patterns found in that file.', items: [] };

  const items: ImportItem[] = incoming.map((raw) => {
    const record = normalize(raw as Partial<PatternRecord>);
    const existing = existingById(record.id);
    let status: ImportItem['status'] = 'new';
    if (existing) {
      const same = JSON.stringify(stripVolatile(existing)) === JSON.stringify(stripVolatile(record));
      status = same ? 'identical' : 'conflict';
    }
    return { record, existing, status };
  });
  return { ok: true, kind, items };
}

export interface ApplyResult {
  added: number;
  replaced: number;
  kept: number;
  skipped: number;
  /** records to persist (insert/replace), and the resolved library */
  toPut: PatternRecord[];
}

/** Resolve import decisions into a set of records to persist. Pure. */
export function resolveImport(
  items: ImportItem[],
  decisions: Record<string, ConflictChoice>
): ApplyResult {
  const res: ApplyResult = { added: 0, replaced: 0, kept: 0, skipped: 0, toPut: [] };
  for (const it of items) {
    const def: ConflictChoice = it.status === 'new' ? 'replace' : it.status === 'identical' ? 'skip' : 'replace';
    const choice = decisions[it.record.id] || def;
    if (choice === 'skip') {
      res.skipped++;
      continue;
    }
    if (choice === 'keepboth') {
      res.toPut.push(normalize({ ...it.record, id: uid(), name: it.record.name + ' (imported)', updatedAt: nowISO() }));
      res.kept++;
      continue;
    }
    if (it.existing) res.replaced++;
    else res.added++;
    res.toPut.push(it.record);
  }
  return res;
}

export function makeBundle(kind: 'pattern' | 'library', patterns: PatternRecord[]): Bundle {
  return { app: 'SKEIN', kind, schema: SCHEMA, exported: nowISO(), patterns };
}
